import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { catchError, EMPTY, interval, map, Subscription, switchMap } from 'rxjs'

import { AuthApi } from '../auth-api/auth-api'

/**
 * AuthRequirementsPoller - Periodic session requirements checker
 *
 * Responsibility: Poll backend to verify user still meets session requirements.
 *
 * Rules:
 * - Polls every 5 minutes while active
 * - Controlled by AuthService (starts after login, stops on logout)
 * - Redirects to page specified by API response if requirements not met
 * - Should be started after successful login
 * - Should be stopped on logout
 *
 * Usage:
 * - Start after login: poller.start()
 * - Stop on logout: poller.stop()
 * - Auto-restart on page refresh if authenticated (via APP_INITIALIZER)
 */
@Injectable({
  providedIn: 'root'
})
export class AuthRequirementsPoller {
  #authApi = inject(AuthApi)
  #router = inject(Router)
  #subscription?: Subscription

  readonly #POLL_INTERVAL = 5 * 60 * 1000 // 5 minutes

  /**
   * Start polling for session requirements.
   * Checks if requirements are met and redirects to specified page if not.
   * Safe to call multiple times (prevents duplicate intervals).
   * Note: AuthService controls when this runs (only while authenticated).
   */
  start(): void {
    this.stop() // Prevent multiple intervals

    this.#subscription = interval(this.#POLL_INTERVAL)
      .pipe(
        switchMap(() =>
          this.#authApi.checkRequirements().pipe(
            catchError(error => {
              console.error('Requirements check failed:', error)
              return EMPTY
            })
          )
        ),
        map(result => {
          if (!result.allowed) {
            this.#router.navigate([result.redirect_page])
          }
        })
      )
      .subscribe()
  }

  /**
   * Stop polling.
   * Call this on logout or when requirements checking is no longer needed.
   */
  stop(): void {
    this.#subscription?.unsubscribe()
    this.#subscription = undefined
  }

  /**
   * Check if poller is currently active.
   * @returns boolean
   */
  isActive(): boolean {
    return this.#subscription !== undefined && !this.#subscription.closed
  }
}
